/** @jsxImportSource @emotion/react */
"use client";

import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { keyframes } from "@emotion/react";
import { I18nMessage } from "@i18n/react";
import { useEffect, useMemo, useState } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const animationScrollLeft = keyframes({
  "0%": { transform: "translateX(0)" },
  "100%": { transform: "translateX(-100%)" },
});
const animationScrollLeftLast = keyframes({
  "0%": { transform: "translateX(100%)" },
  "100%": { transform: "translateX(0)" },
});
const animationScrollRight = keyframes({
  "0%": { transform: "translateX(0)" },
  "100%": { transform: "translateX(100%)" },
});
const animationScrollRightLast = keyframes({
  "0%": { transform: "translateX(-100%)" },
  "100%": { transform: "translateX(0)" },
});

const cssDivContainer: Interpolation<ThemeType> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.s,
});
const cssDivContentContainer: FunctionInterpolation<{
  direction: "left" | "right";
  speed: number;
}> = (props) => ({
  alignItems: "center",
  animationDuration: `${props.speed}s`,
  animationIterationCount: "infinite",
  animationName:
    props.direction === "left" ? animationScrollLeft : animationScrollRight,
  animationTimingFunction: "linear",
  display: "flex",
  flexShrink: 0,
  minWidth: "100%",
  ":last-child": {
    animationName:
      props.direction === "left"
        ? animationScrollLeftLast
        : animationScrollRightLast,
    left: props.direction === "left" ? 0 : undefined,
    position: "absolute",
    right: props.direction === "right" ? 0 : undefined,
    top: 0,
  },
});
const cssDivMarquee: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.s,
  overflow: "hidden",
  position: "relative",
  width: "fit-content",
});
const cssDivOverlay =
  (props: { isVisible: boolean }): Interpolation<ThemeType> =>
  (theme) => ({
    alignItems: "center",
    backgroundColor: theme.shades.shade60,
    borderRadius: 8,
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    justifyContent: "center",
    opacity: props.isVisible ? 1 : 0,
    padding: 4,
    textAlign: "center",
    transition: "opacity 0.375s ease-in-out",
    width: "100%",
  });
const cssLi: FunctionInterpolation<{ src: string }> = (props) => ({
  backgroundImage: `url(${props.src})`,
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
  borderRadius: 8,
  height: 80,
  marginRight: 8,
  width: 120,
  ["@media(min-width: 540px)"]: { width: 140 },
});
const cssTextDescription: Interpolation<ThemeType> = (theme) => ({
  marginTop: theme.spacing.s,
  textTransform: "uppercase",
});

const IMAGES = {
  1: {
    description: "category_page_praylists",
    title: "general_word_humility",
  },
  2: {
    description: "category_page_sleep",
    title: "category_pages_jonathan_roumie",
  },
  3: {
    description: "meditate_tabs_category_minutes",
    title: "general_word_temptations",
  },
  4: {
    description: "category_pages_dailies",
    title: "general_words_divine_mercy",
  },
  5: {
    description: "category_pages_dailies",
    title: "goal_title_meditation",
  },
  6: {
    description: "category_page_challenges",
    title: "general_word_intro",
  },
  7: {
    description: "category_page_challenges",
    title: "category_page_saints",
  },
  8: {
    description: "category_page_sleep",
    title: "night_prayer",
  },
  9: {
    description: "category_page_praylists",
    title: "general_word_calm",
  },
  10: {
    description: "general_words_guest_sessions",
    title: "category_page_fr_mike_schmitz",
  },
  11: {
    description: "category_pages_dailies",
    title: "category_page_rosary",
  },
  12: {
    description: "category_page_sleep",
    title: "category_page_saints",
  },
  13: {
    description: "general_word_spanish",
    title: "general_word_guadalupe",
  },
  14: {
    description: "category_pages_dailies",
    title: "onboarding_daily_gospel",
  },
  15: {
    description: "category_page_challenges",
    title: "general_word_stations",
  },
  16: {
    description: "meditate_tabs_category_minutes",
    title: "general_words_jesus_i_trust_in_you",
  },
  17: {
    description: "category_pages_dailies",
    title: "goal_title_examen",
  },
} as const;
const IMAGES_HIGHLIGHT_INDEX_ORDER = [
  10, 15, 4, 9, 5, 8, 6, 16, 1, 2, 17, 7, 3, 12, 4, 11, 5, 11, 13, 6, 10, 1, 14,
  2, 3,
];
const MARQUEES = [
  { direction: "left", keys: [1, 2, 3, 4, 5, 6], speed: 32.5 },
  { direction: "right", keys: [7, 8, 9, 10, 11], speed: 55 },
  { direction: "left", keys: [12, 13, 14, 15, 16, 17], speed: 60 },
] as const;

/** One tile per entry of IMAGES, in key order */
export const IMAGES_MARQUEE_TILE_COUNT = 17;

/**
 * This package ships no artwork of its own, so a consumer has to say where its
 * copies live. Building the list here keeps callers to one line while leaving
 * the dependency visible at the call site.
 */
export const imagesMarqueeSrcs = (basePath: string) =>
  Array.from(
    { length: IMAGES_MARQUEE_TILE_COUNT },
    (_, index) => `${basePath}/img-${index + 1}.webp`,
  );

export type ImagesMarqueeProps = {
  /** One src per tile, ordered to match IMAGES */
  srcs: string[];
};

export const ImagesMarquee = ({ srcs }: ImagesMarqueeProps) => {
  const [imageHighlightIndex, setImageHighlightIndex] = useState<number>();
  const [isImageHighlightInTransition, setIsImageHighlightInTransition] =
    useState(false);

  const imageHighlightKey = useMemo(
    () => IMAGES_HIGHLIGHT_INDEX_ORDER[imageHighlightIndex],
    [imageHighlightIndex],
  );

  /**
   * Driven off the index rather than one long-lived interval: with empty deps
   * the end-of-sequence guard read a stale index, so the highlight ran past
   * the last entry and every tile went dark.
   */
  useEffect(() => {
    if (imageHighlightIndex === undefined) {
      const timeout = setTimeout(() => setImageHighlightIndex(0), 325);
      return () => clearTimeout(timeout);
    }

    if (imageHighlightIndex === IMAGES_HIGHLIGHT_INDEX_ORDER.length - 1) return;

    const timeoutTransition = setTimeout(
      () => setIsImageHighlightInTransition(true),
      1600,
    );
    const timeoutAdvance = setTimeout(() => {
      setImageHighlightIndex((previous) => previous + 1);
      setIsImageHighlightInTransition(false);
    }, 2250);

    return () => {
      clearTimeout(timeoutTransition);
      clearTimeout(timeoutAdvance);
    };
  }, [imageHighlightIndex]);

  return (
    <div css={cssDivContainer}>
      {MARQUEES.map(({ direction, keys, speed }, index) => (
        <div css={cssDivMarquee} key={index}>
          {[keys, keys].map((items, index) => (
            <ul
              aria-hidden={index === 1}
              css={cssDivContentContainer({ direction, speed })}
              key={index}
            >
              {items.map((key) => (
                <li css={cssLi({ src: srcs[key - 1] })} key={key}>
                  <div
                    css={cssDivOverlay({
                      isVisible:
                        key === imageHighlightKey &&
                        !isImageHighlightInTransition,
                    })}
                  >
                    <Text css={cssTextDescription} size="s" type="detail">
                      <I18nMessage message={IMAGES[key].description} />
                    </Text>
                    <Text size="s" type="title">
                      <I18nMessage message={IMAGES[key].title} />
                    </Text>
                  </div>
                </li>
              ))}
            </ul>
          ))}
        </div>
      ))}
    </div>
  );
};
